import { leaderboardStatLabels } from './leaderboard-stats'
import type {
  AwardThresholdKind,
  AwardType,
  HunterEliminationMode,
  HunterRewardType,
  ChannelFailReason,
  ChannelKind,
  ChannelRedemptionStatus,
  ContentCategory,
  EntryKind,
  GameEventKind,
  GameKind,
  GamePlayerStatus,
  GameRequestKind,
  GameRequestStatus,
  GameStatus,
  GameTableStatus,
  GatewayJobKind,
  Gender,
  GoodsItemKind,
  ImportKind,
  LeaderboardGrain,
  MemberLevel,
  MemberPointRecordKind,
  MemberVoucherRecordActorKind,
  MemberVoucherRecordKind,
  MemberVoucherRecordReason,
  MemberRole,
  MemberStatus,
  MotivationAwardType,
  MotivationChipUnit,
  SettlementCadence,
  SettlementProtectionRule,
  SettlementStatus,
  OrderAdjustmentKind,
  OrderCancelReason,
  OrderStatus,
  PaymentMethod,
  RefundStatus,
  ProductKind,
  PurchaseLimitPeriod,
  LeaderboardAwardCadence,
  LeaderboardAwardType,
  RankingDimension,
  RequestAlertMode,
  SeatKind,
  SiteLeadStatus,
  SiteLeadStoreSize,
  StaffRole,
  TenantPlan,
  DisplayGrantKind,
  SubscriptionCycle,
  SubscriptionInvoiceStatus,
  SubscriptionOrderStatus,
  VoucherSource,
} from './index'
import {
  voucherScopeKindLabels,
  voucherValidityKindLabels,
  voucherValidityUnitLabels,
} from './voucher'
import type { ListingStatus } from './listing-status'
import type { SeasonStatus } from './season'

const bossPeriodLabels = {
  today: '本日',
  week: '本周',
  month: '本月',
  lastWeek: '上一周',
  lastMonth: '上个月',
  quarter: '本季度',
  year: '本年',
} as const

const gamePlayerStatusLabels = {
  seated: '在座',
  waiting: '待入座',
  eliminated: '出局',
} satisfies Record<GamePlayerStatus, string>

const channelKindLabels = {
  douyin: '抖音',
  meituan: '美团',
} satisfies Record<ChannelKind, string>

const contentCategoryLabels = {
  activity_notice: '活动通知',
  tournament_announcement: '赛事公告',
  rule_guide: '规则说明',
} satisfies Record<ContentCategory, string>

const genderLabels = {
  male: '男',
  female: '女',
  unknown: '未知',
} satisfies Record<Gender, string>

const entryKindLabels = {
  entry: '入场',
  rebuy: '续场',
} satisfies Record<EntryKind, string>

const promotionOfferLabels = {
  first_entry_price: '本场首次报名促销价',
  first_entry_half: '本场首次报名半价',
  first_entry_free: '本场首次报名免单',
  stack_double: '报名双倍计分',
} as const

const memberLevelLabels = {
  normal: '普通会员',
  vip: 'VIP',
} satisfies Record<MemberLevel, string>

const rankingDimensionLabels = {
  champion: '冠军',
  podium: '入围',
  points: '积分',
  hunter: '猎人',
  achievement: '成就',
} satisfies Record<RankingDimension, string>

const leaderboardGrainLabels = {
  lifetime: '总榜',
  week: '周榜',
  month: '月榜',
  year: '年榜',
  season: '赛季',
} satisfies Record<LeaderboardGrain, string>

const seasonStatusLabels = {
  upcoming: '未开始',
  current: '进行中',
  ended: '已结束',
} satisfies Record<SeasonStatus, string>

/** 小程序只引用这份，避免把角色、座位种类里的 dealer 字面量打进微信包 */
export {
  entryKindLabels,
  promotionOfferLabels,
  rankingDimensionLabels,
  leaderboardStatLabels,
}

export const clientLabels = {
  bossPeriod: bossPeriodLabels,
  gamePlayerStatus: gamePlayerStatusLabels,
  channelKind: channelKindLabels,
  contentCategory: contentCategoryLabels,
  gender: genderLabels,
  memberLevel: memberLevelLabels,
  rankingDimension: rankingDimensionLabels,
  leaderboardStat: leaderboardStatLabels,
}

export const labels = {
  siteLeadStatus: {
    new: '待联系',
    contacted: '已联系',
    qualified: '有效线索',
    closed: '已关闭',
  } satisfies Record<SiteLeadStatus, string>,
  siteLeadStoreSize: {
    preparing: '筹备中',
    '1-5': '1–5 桌',
    '6-12': '6–12 桌',
    '13+': '13 桌以上',
  } satisfies Record<SiteLeadStoreSize, string>,
  rankingDimension: rankingDimensionLabels,
  leaderboardStat: leaderboardStatLabels,
  leaderboardAwardCadence: {
    month: '每月',
    year: '每年',
  } satisfies Record<LeaderboardAwardCadence, string>,
  leaderboardAwardType: {
    ticket: '比赛券',
    voucher: '其他票券',
  } satisfies Record<LeaderboardAwardType, string>,
  leaderboardGrain: leaderboardGrainLabels,
  seasonStatus: seasonStatusLabels,
  staffRole: {
    platform: '平台',
    admin: '管理员',
    dealer: '裁判',
    manager: '店长',
    demo: '演示',
  } satisfies Record<StaffRole, string>,
  tenantPlan: {
    trial: '试用',
    standard: '基础版',
    pro: '专业版',
    display: '大屏版',
  } satisfies Record<TenantPlan, string>,
  displayGrantKind: {
    stick: 'U 盘',
    browser: '浏览器',
  } satisfies Record<DisplayGrantKind, string>,
  bossPeriod: bossPeriodLabels,
  gameKind: {
    tournament: '锦标赛',
    normal: '常规赛',
  } satisfies Record<GameKind, string>,
  awardThresholdKind: {
    player_count: '人数',
    hand_count: '场数',
  } satisfies Record<AwardThresholdKind, string>,
  awardType: {
    point: '积分',
    ticket: '比赛券',
    voucher: '票券',
  } satisfies Record<AwardType, string>,
  hunterRewardType: {
    point: '积分',
    ticket: '比赛券',
    voucher: '票券',
  } satisfies Record<HunterRewardType, string>,
  hunterElimination: {
    select: '选择处决玩家',
    count: '输入赏金数量',
  } satisfies Record<HunterEliminationMode, string>,
  entryKind: entryKindLabels,
  productKind: {
    ticket: '票券',
    goods: '商品',
  } satisfies Record<ProductKind, string>,
  purchaseLimitPeriod: {
    day: '每天',
    week: '每周',
    month: '每月',
    year: '每年',
    lifetime: '永久',
  } satisfies Record<PurchaseLimitPeriod, string>,
  goodsItemKind: {
    beverage: '酒水',
    food: '小食',
    service: '服务',
  } satisfies Record<GoodsItemKind, string>,
  paymentMethod: {
    wechat: '微信支付',
    alipay: '支付宝',
    cash: '现金',
    lakala: '聚合支付',
  } satisfies Record<PaymentMethod, string>,
  orderStatus: {
    unpaid: '未支付',
    paid: '已完成',
    refund_requested: '申请退款中',
    refunding: '退款中',
    cancelled: '已取消',
    refunded: '已退款',
  } satisfies Record<OrderStatus, string>,
  orderCancelReason: {
    member: '已取消',
    timeout: '已超时取消',
  } satisfies Record<OrderCancelReason, string>,
  refundStatus: {
    requested: '待确认',
    pending: '待受理',
    processing: '退款中',
    succeeded: '已退款',
    abnormal: '退款异常',
    failed: '退款失败',
  } satisfies Record<RefundStatus, string>,
  orderDeliveryStatus: {
    none: '未生成',
    pending: '待确认',
    confirmed: '已交分',
    cancelled: '已取消',
  },
  voucherScopeKind: voucherScopeKindLabels,
  voucherValidityKind: voucherValidityKindLabels,
  voucherValidityUnit: voucherValidityUnitLabels,
  orderAdjustmentKind: {
    ticket: '票券优惠',
    comp: '人工免单',
    promotion: '促销',
    new_customer: '新客入场免费',
  } satisfies Record<OrderAdjustmentKind, string>,
  promotionOffer: promotionOfferLabels,
  promotionOfferHint: {
    first_entry_price:
      '尚未入场的首次报名按此价，续场原价。填 0 即免单，不占比赛券',
    stack_double: '顾客照付一份，买码计分翻倍',
  },
  gameRequestKind: {
    entry: '入场',
    rebuy: '续场',
  } satisfies Record<GameRequestKind, string>,
  gamePlayerStatus: gamePlayerStatusLabels,
  gameRequestStatus: {
    pending: '待确认',
    confirmed: '已确认',
    cancelled: '已取消',
  } satisfies Record<GameRequestStatus, string>,
  gameEventKind: {
    player_seated: '玩家入座',
    player_chips_added: '玩家计分增加',
    player_chips_removed: '玩家计分减少',
    player_seat_changed: '玩家换座',
    player_eliminated: '玩家出局',
    player_transferred_out: '玩家转出',
    player_transferred_in: '玩家转入',
    dealer_changed: '裁判换班',
    motivation_granted: '发放激励',
    motivation_revoked: '撤销激励',
  } satisfies Record<GameEventKind, string>,
  seatKind: {
    player: '顾客位',
    dealer: '裁判位',
  } satisfies Record<SeatKind, string>,
  gameStatus: {
    preparing: '准备中',
    running: '进行中',
    finished: '已结束',
  } satisfies Record<GameStatus, string>,
  gameTableStatus: {
    running: '进行中',
    closed: '已关闭',
  } satisfies Record<GameTableStatus, string>,
  tableOccupancy: {
    idle: '空闲',
    running: '进行中',
  },
  voucherSource: {
    motivation: '激励发放',
    award: '排名奖励',
    manual: '手工发放',
    point_exchange: '积分兑换',
    refund: '退款补发',
    channel: '渠道核销',
    hunter: '猎人奖励',
    new_customer: '新客入场',
    purchase: '购买赠送',
  } satisfies Record<VoucherSource, string>,
  channelKind: channelKindLabels,
  channelAuth: {
    authorized: '已授权',
    unauthorized: '未授权',
  },
  channelRedemptionStatus: {
    pending: '核销中',
    succeeded: '已核销',
    failed: '失败',
  } satisfies Record<ChannelRedemptionStatus, string>,
  channelFailReason: {
    invalid_code: '券码不存在或已被使用，请核对后重试',
    offer_unmapped: '本店尚未配置该套餐的兑换规则，请联系前台',
    template_unusable: '本店该套餐的兑换券暂不可用，请联系前台',
    multi_use_unsupported: '该券为量贩券或次卡，暂不支持自助核销，请联系前台',
    credential_expired: '本店渠道授权已失效，请联系前台',
    platform_error: '渠道服务暂时不可用，请稍后重试',
  } satisfies Record<ChannelFailReason, string>,
  contentCategory: contentCategoryLabels,
  motivationAwardType: {
    chips: '计分牌',
    point: '积分',
    voucher: '票券',
    ticket: '比赛券',
  } satisfies Record<MotivationAwardType, string>,
  motivationChipUnit: {
    bb: '按基准',
    chips: '按计分',
  } satisfies Record<MotivationChipUnit, string>,
  memberPointRecordKind: {
    motivation: '激励',
    carryover: '结转',
    incentive: '激励',
    reversal: '撤回',
    ticket_exchange: '积分换票',
    month_expiration: '积分清零',
    adjustment: '人工调整',
    hunter: '猎人奖励',
    purchase: '购买赠送',
  } satisfies Record<MemberPointRecordKind, string>,
  memberVoucherRecordKind: {
    increased: '增加',
    decreased: '减少',
  } satisfies Record<MemberVoucherRecordKind, string>,
  memberVoucherRecordReason: {
    manual: '手工调整',
    motivation: '激励',
    award: '排名奖励',
    channel: '渠道核销',
    point_exchange: '积分兑换',
    refund: '退款补发',
    used: '使用',
    revoked: '撤销激励',
    expired: '过期',
    transferred: '转移',
    hunter: '猎人奖励',
    new_customer: '新客入场',
    purchase: '购买赠送',
    refunded: '退款收回',
  } satisfies Record<MemberVoucherRecordReason, string>,
  memberVoucherRecordActorKind: {
    staff: '员工',
    system: '系统',
  } satisfies Record<MemberVoucherRecordActorKind, string>,
  settlementStatus: {
    pending: '待执行',
    running: '执行中',
    completed: '已完成',
    failed: '失败',
  } satisfies Record<SettlementStatus, string>,
  settlementCadence: {
    weekly: '每周',
    monthly: '每月',
    bimonthly: '每双月',
    semiannual: '每半年',
    quarterly: '每季度',
    yearly: '每一年',
  } satisfies Record<SettlementCadence, string>,
  settlementProtectionRule: {
    all: '全部保护',
    remainder: '剩余保护',
  } satisfies Record<SettlementProtectionRule, string>,
  importKind: {
    members: '会员',
    manual_games: '赛事',
    manual_leaderboards: '历史榜单',
  } satisfies Record<ImportKind, string>,
  gender: genderLabels,
  memberLevel: memberLevelLabels,
  memberRole: {
    customer: '顾客',
    staff: '员工',
    agent: '代理',
  } satisfies Record<MemberRole, string>,
  memberStatus: {
    active: '正常',
    disabled: '停用',
  } satisfies Record<MemberStatus, string>,
  listingStatus: {
    active: '可用',
    disabled: '停用',
    deleted: '已删除',
  } satisfies Record<ListingStatus, string>,
  gatewayJobKind: {
    text: '文本',
    image: '图片',
  } satisfies Record<GatewayJobKind, string>,
  requestAlertMode: {
    immediate: '立刻提醒',
    timeout: '超时提醒',
  } satisfies Record<RequestAlertMode, string>,
  subscriptionCycle: {
    monthly: '按月付费',
    yearly: '按年付费',
    perpetual: '永久授权',
  } satisfies Record<SubscriptionCycle, string>,
  subscriptionOrderStatus: {
    pending: '待支付',
    paid: '已支付',
    closed: '已关闭',
  } satisfies Record<SubscriptionOrderStatus, string>,
  subscriptionInvoiceStatus: {
    requested: '已申请',
    issued: '已开具',
  } satisfies Record<SubscriptionInvoiceStatus, string>,
} as const
